import React, {
  useState,
  useEffect,
  useCallback,
  Fragment,
  createRef
} from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'
import ReactCSSTransitionGroup from 'react-addons-css-transition-group'
import Layout from '../Layout'
import Link from '../Link'
import FullWidthSection from '../FullWidthSection'
import { addEventListener, removeEventListener } from '../../helpers/dom'
import { getHashParam, setHashParam } from '../../helpers/url'
import { timing } from '../../helpers/analytics'
import { FaRegEye, FaInfo } from 'react-icons/fa'
import Image from '../Image'
import Query from './Query'
import Video from '../Video'
import Markdown from '../Markdown'
import Loader from '../Loader'
import Modal, { ModalTrigger } from '../Modal'
import { HOME_IFRAME_TIMING } from '../../constants/analytics'

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    margin: 0;
  }
`

const UIControls = styled.div`
  > span {
    > div {
      &.ui-enter {
        transform: translateY(100%);
      }

      &.ui-enter-active {
        transition: transform 0.3s ease-in-out;
        transform: translateY(0);
      }

      &.ui-leave {
        transform: translateY(0);
      }

      &.ui-leave-active {
        transition: transform 0.3s ease-in-out;
        transform: translateY(100%);
      }
    }
  }
`

const glow = keyframes`
  0% {
    opacity: 0.3;
    box-shadow: 0 0 1rem 0.6rem ${({ theme }) => theme.colors.interactive};
  }

  50% {
    opacity: 1;
    box-shadow: 0 0 1.8rem 1.8rem ${({ theme }) => theme.colors.interactive};
  }

  100% {
    opacity: 0.3;
    box-shadow: 0 0 1rem 0.6rem ${({ theme }) => theme.colors.interactive};
  }
`

const ViewPanelToggle = styled.div`
  position: absolute;
  padding-bottom: 2rem;
  right: 3rem;
  bottom: 0;

  a {
    display: block;
    width: 6rem;
    height: 6rem;
    padding: 3rem;
    font-size: 3rem;
    border-radius: 3rem;
    color: ${({ theme }) => theme.colors.secondaryText};
    border: 0.1rem solid ${({ theme }) => theme.colors.secondaryText};
    background-color: transparent;
    position: relative;
    line-height: 1;
    margin-top: 2rem;

    > div {
      position: absolute;
      top: 50%;
      left: 50%;
      display: block;
      width: 6rem;
      height: 6rem;
      padding: 3rem;
      border-radius: 3rem;
      box-shadow: 0 0 1rem 0.6rem ${({ theme }) => theme.colors.interactive};
      animation: ${glow} 2.8s ease-in-out 0s infinite;
      transform: scale(1) translate(-50%, -50%);
      transform-origin: 0 0;
    }

    > svg {
      transform: translate(-50%, -50%);
    }
  }
`

const ViewPanelScreen = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background-color: transparent;
`

const ViewPanel = styled.div`
  position: absolute;
  bottom: 0;
  padding-bottom: 2rem;
  width: 100%;
  text-align: center;
  z-index: 1;

  > a {
    display: inline-block;
    padding: 2rem;
    vertical-align: middle;

    > div {
      overflow: hidden;
      width: 8rem;
      height: 8rem;
      border-radius: 50%;
      border: 0.1rem solid ${({ theme }) => theme.colors.text};
      transform: scale(1);
      transition: transform 0.2s ease-in-out;

      img {
        width: 8rem;
        margin: 0;
      }
    }

    @media not all and (hover: none) {
      &:hover {
        > div {
          transform: scale(1.4);
        }
      }
    }
  }
`

const ModalContent = styled.div`
  margin-bottom: 5rem;
  padding: 2rem;
  position: absolute;
  top: 50%;
  width: 100%;

  @media screen and (max-width: 500px) {
    padding: 0;
    top: auto;
    position: static;
    width: auto;
  }
`

const ModalContentInner = styled.div`
  display: block;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 50rem;

  @media screen and (max-width: 500px) {
    left: auto;
    top: auto;
    transform: none;
    position: static;
    width: auto;
    max-width: none;
  }
`

const IFrameContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100vh;
  opacity: ${({ loaded }) => (loaded ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  transition-delay: 0.1s;
`

const IFrameInner = styled.div`
  height: 100vh;
  width: 100%;
  min-width: ${({ hasOverflow }) => (hasOverflow ? 36 : 0)}rem;

  &.under-nav {
    padding-top: 9rem;

    @media screen and (max-width: 600px) {
      padding-top: 7.8rem;
    }
  }
`

const LoadingContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

const HomePageContent = ({ visualizations }) => {
  const nameToSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '-')
  const getVisualizationFromHash = () => {
    const visualizationName = getHashParam('visualization')
    return (
      visualizations.filter(
        (visualization) => visualizationName === nameToSlug(visualization.name)
      )[0] || null
    )
  }

  const iframeRef = createRef()
  const [uiPanelVisible, setUIPanelVisible] = useState(false)
  const [visualization, setVisualization] = useState(
    getVisualizationFromHash() || visualizations[0]
  )
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isClient, setIsClient] = useState(false)

  const onHashChange = useCallback(() => {
    let change = false
    const nextVisualization = getVisualizationFromHash() || visualization
    try {
      if (JSON.stringify(nextVisualization) !== JSON.stringify(visualization))
        change = true
    } catch (err) {
      change = true
    }

    if (change) {
      setIsLoading(null)
      setIsLoaded(false)
      setVisualization(nextVisualization)
    }
  }, [visualization])

  const showUIPanel = (e) => {
    e.preventDefault()
    setUIPanelVisible(true)
  }

  const thumbnailVisualizationClick = (visualization) => (e) => {
    e.preventDefault()
    setUIPanelVisible(false)
    setHashParam('visualization', nameToSlug(visualization.name))
  }

  const iframeOnLoad = (start, visualization) => () => {
    timing({
      category: HOME_IFRAME_TIMING,
      label: visualization.src,
      value: Date.now() - start,
      variable: visualization.name
    })
    setIsLoading(false)
    setIsLoaded(true)
  }

  const isUnderNav = (src) =>
    src.match(
      /^(https?:)?\/\/input-output-hk.github.io\/game-of-life-ca\/?.*?$/
    )

  useEffect(() => {
    if (!isClient) setIsClient(true)
    if (!isLoaded && !isLoading && iframeRef.current) {
      setIsLoading(true)
      iframeRef.current.addEventListener(
        'load',
        iframeOnLoad(Date.now(), visualization)
      )
    }

    removeEventListener('hashchange', onHashChange)
    addEventListener('hashchange', onHashChange)

    return () => {
      removeEventListener('hashchange', onHashChange)
    }
  }, [visualization, iframeRef, isLoading])

  return (
    <Layout>
      <FullWidthSection fullWidthContent>
        <Container>
          {isClient && (
            <IFrameContainer loaded={isLoaded}>
              {/* Game of life visualisation doesn't work correctly on small devices so the min width needs restricted and overflow hidden */}
              <IFrameInner
                className={isUnderNav(visualization.src) ? 'under-nav' : ''}
                hasOverflow={visualization.src.match(
                  /^(https?:)?(\/\/)?static.iohk.io\/gol2\/?.*?$/
                )}
              >
                <iframe ref={iframeRef} src={visualization.src} />
              </IFrameInner>
            </IFrameContainer>
          )}
          {isLoading && !isLoaded && (
            <LoadingContainer>
              <Loader />
            </LoadingContainer>
          )}
          <UIControls>
            <ReactCSSTransitionGroup
              transitionName="ui"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {!uiPanelVisible && (
                <ViewPanelToggle key="toggle">
                  {(visualization.description || visualization.video_id) && (
                    <Fragment>
                      <ModalTrigger name="visualization-info">
                        <FaInfo />
                      </ModalTrigger>
                      <Modal opacity={0.8} fullScreen name="visualization-info">
                        <ModalContent>
                          <ModalContentInner>
                            {visualization.video_id && (
                              <Video id={visualization.video_id} height={200} />
                            )}
                            <h2>{visualization.label}</h2>
                            <hr />
                            <Markdown source={visualization.description} />
                          </ModalContentInner>
                        </ModalContent>
                      </Modal>
                    </Fragment>
                  )}
                  <Link
                    tracking={{ label: 'home_page_show_ui_panel' }}
                    href="#"
                    onClick={showUIPanel}
                  >
                    <div />
                    <FaRegEye />
                  </Link>
                </ViewPanelToggle>
              )}
              {uiPanelVisible && (
                <ViewPanelScreen
                  key="panel-screen"
                  onClick={() => setUIPanelVisible(false)}
                />
              )}
              {uiPanelVisible && (
                <ViewPanel key="panel" onClick={() => setUIPanelVisible(false)}>
                  {visualizations.map((visualization) => (
                    <Link
                      tracking={{
                        label: `home_page_visualisation_${visualization.src}`
                      }}
                      onClick={thumbnailVisualizationClick(visualization)}
                      title={visualization.name}
                      href="#"
                      key={visualization.src}
                    >
                      <div>
                        <Image
                          src={`${visualization.thumbnail}-/progressive/yes/`}
                          size={80}
                          alt={visualization.name}
                        />
                      </div>
                    </Link>
                  ))}
                </ViewPanel>
              )}
            </ReactCSSTransitionGroup>
          </UIControls>
        </Container>
      </FullWidthSection>
    </Layout>
  )
}

HomePageContent.propTypes = {
  visualizations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      thumbnail: PropTypes.string.isRequired,
      video_id: PropTypes.string,
      subtitle: PropTypes.string,
      description: PropTypes.string
    })
  )
}

export default () => (
  <Query
    render={({ visualizations }) => (
      <HomePageContent visualizations={visualizations} />
    )}
  />
)
